import { memo } from 'react';

/**
 * Portraits are identity chips, not pseudo-people. The old illustrated faces
 * read poorly at seat size and made the table feel like a casual game. These
 * are small, deterministic pieces of the game's visual language: a monogram,
 * a jewel-tone gradient, and an orbital line unique to the selected seed.
 */

/**
 * Practice opponents draw from these, in seat order. Ten, so a six-seat table
 * can be filled around one human with characters to spare; the server repeats a
 * character with a numbered handle rather than refusing the seat.
 */
export const BOT_CHARACTER_KEYS = [
  'nila', 'arun', 'devi', 'kabir', 'meera',
  'ravi', 'sana', 'jai', 'ila', 'tara',
];

/** Curated skins offered by the picker — never a practice opponent's. */
export const HUMAN_CHARACTER_KEYS = [
  'db:jade', 'db:violet', 'db:ember', 'db:sapphire',
  'db:rose', 'db:gold', 'db:onyx', 'db:aurora',
];

const PALETTES = [
  ['#153c36', '#38c6a4'],
  ['#2a1b54', '#9d78ff'],
  ['#4a1e2d', '#ff6e9d'],
  ['#102c4d', '#54b7ff'],
  ['#44210f', '#ffad54'],
  ['#342215', '#e7bd67'],
  ['#1b1d2b', '#8693b9'],
  ['#203446', '#6de2d1'],
] as const;

function seedOf(character: string): string {
  return character.startsWith('db:') ? character.slice(3) : character;
}

function hashOf(value: string): number {
  let hash = 2166136261;
  for (let i = 0; i < value.length; i++) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

function initialsOf(handle: string): string {
  const initials = handle
    .trim()
    .split(/[\s_-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => [...word][0] ?? '')
    .join('')
    .toLocaleUpperCase();
  return initials || '·';
}

export interface PortraitProps {
  /** An avatar skin (`db:<seed>` or a legacy key). */
  character: string | null | undefined;
  handle: string;
  size?: number;
  dim?: boolean;
}

export const Portrait = memo(function Portrait({
  character,
  handle,
  size = 44,
  dim,
}: PortraitProps) {
  const seed = seedOf(character || handle || 'rummy');
  const hash = hashOf(seed);
  const palette = PALETTES[hash % PALETTES.length]!;
  const orbit = hash % 3;

  return (
    <span
      className={`portrait portrait--glyph${dim ? ' portrait--dim' : ''}`}
      style={{
        width: size,
        height: size,
        background: `linear-gradient(145deg, ${palette[0]}, ${palette[1]})`,
      }}
      aria-hidden="true"
    >
      <svg viewBox="0 0 64 64" focusable="false" aria-hidden="true">
        <circle cx="32" cy="32" r="25" fill="#ffffff" fillOpacity="0.07" />
        {orbit === 0 && <path d="M7 38C18 12 43 11 58 28" />}
        {orbit === 1 && <path d="M11 21C34 7 55 24 52 51" />}
        {orbit === 2 && <path d="M6 30C20 52 47 55 58 23" />}
        <circle cx={orbit === 0 ? 49 : orbit === 1 ? 16 : 47} cy={orbit === 2 ? 21 : 45} r="3" />
        <text x="32" y="40" textAnchor="middle" fontSize="25" fontWeight="700">
          {initialsOf(handle)}
        </text>
      </svg>
    </span>
  );
});
